import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, DollarSign, AlertTriangle, TrendingUp, RefreshCw } from 'lucide-react'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, StatCard, Button, Skeleton } from '@/components/ui'
import { cn } from '@/utils'
import { useDashboardSummary } from '@/hooks/useDashboardSummary'
import type { DashboardRange } from '@/types/dashboard-summary'
import { DashboardTrendChart } from './Dashboard/DashboardTrendChart'

const RANGES: { value: DashboardRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
]

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100)
  } catch {
    return `${(cents / 100).toFixed(0)} ${currency}`
  }
}

export function DashboardPage() {
  const [range, setRange] = useState<DashboardRange>('week')
  const navigate = useNavigate()
  const { data, isLoading, isError } = useDashboardSummary(range)

  return (
    <PageFrame width="detail" className="space-y-6">
      <PageHeader
        title="Dashboard"
        icon={TrendingUp}
        sticky
        subtitle="Your business at a glance — handled by your AI."
        actions={
          <div className="inline-flex rounded-lg border border-border p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setRange(r.value)}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  range === r.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        }
      />

      {isError && (
        <Card className="border-destructive/50 bg-destructive/10">
          <div className="flex items-center justify-between p-4">
            <p className="text-destructive">Couldn't load dashboard data.</p>
            <Button variant="secondary" size="sm" onClick={() => window.location.reload()} leftIcon={<RefreshCw className="w-4 h-4" />}>
              Retry
            </Button>
          </div>
        </Card>
      )}

      {isLoading && (
        <div data-testid="dashboard-loading" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <div className="h-24 p-4">
                <Skeleton className="h-full rounded-sm" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={MessageSquare}
              accent="primary"
              label="Conversations handled by AI"
              value={String(data.conversations.aiHandled)}
              sublabel={`of ${data.conversations.total} total`}
            />
            {data.revenue && (
              <StatCard
                icon={DollarSign}
                accent="success"
                label="Revenue (month to date)"
                value={formatMoney(data.revenue.mtdCents, data.revenue.currency)}
                sublabel={`${data.revenue.ordersMtd} orders`}
              />
            )}
            <StatCard
              icon={AlertTriangle}
              accent="destructive"
              label="Needs attention"
              value={String(data.needsAttention.humanConversations)}
              sublabel={`${data.needsAttention.unread} unread · ${data.needsAttention.newOrders} new orders`}
              actionLabel="Open inbox"
              onAction={() => navigate('/inbox')}
            />
            <StatCard
              icon={TrendingUp}
              accent="warning"
              label="Total conversations"
              value={String(data.conversations.total)}
              sublabel={`${data.conversations.humanHandled} handled by a person`}
            />
          </div>

          {/* Trend */}
          <Card>
            <div className="p-4">
              <h2 className="mb-4 text-sm font-semibold text-foreground">
                Trends over time
              </h2>
              <DashboardTrendChart
                points={data.trend.points}
                includeRevenue={!!data.revenue}
                currency={data.revenue?.currency ?? 'USD'}
                height={220}
              />
            </div>
          </Card>
        </>
      )}
    </PageFrame>
  )
}
